import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Leaf, Zap, TrendingDown, Info, Star } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { EnergyEfficiencyImpact } from '@/types/analytics';
import { formatCurrency } from '@/components/calculator/utils/formatters';

interface EnergyEfficiencyCardProps {
  impact: EnergyEfficiencyImpact;
  onHersIndexChange: (value: number) => void;
  onBaselineChange: (value: number) => void;
  onUtilityCostChange: (value: number) => void;
}

export const EnergyEfficiencyCard: React.FC<EnergyEfficiencyCardProps> = ({
  impact,
  onHersIndexChange,
  onBaselineChange,
  onUtilityCostChange
}) => {
  const [localHersIndex, setLocalHersIndex] = useState(impact.hersIndex.toString());
  const [localBaseline, setLocalBaseline] = useState(impact.baselineHersIndex.toString());
  
  const getEfficiencyRating = (hersIndex: number): { label: string; color: string } => {
    if (hersIndex <= 50) return { label: 'Exceptional', color: 'bg-green-500 text-slate-900' };
    if (hersIndex <= 70) return { label: 'Excellent', color: 'bg-green-400 text-slate-900' };
    if (hersIndex <= 85) return { label: 'Very Good', color: 'bg-cyan-400 text-slate-900' };
    if (hersIndex <= 100) return { label: 'Good', color: 'bg-blue-400 text-slate-900' };
    return { label: 'Standard', color: 'bg-slate-500 text-slate-100' };
  };

  const rating = getEfficiencyRating(impact.hersIndex);

  return (
    <Card className="bg-slate-900/95 border-slate-700">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-slate-100">
          <Leaf className="w-5 h-5 text-green-400" />
          Energy Efficiency Impact Analysis
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Info className="w-4 h-4 text-slate-400" />
              </TooltipTrigger>
              <TooltipContent className="max-w-sm bg-slate-800 text-slate-100 border-slate-700">
                <p className="text-sm">
                  HERS Index measures home energy efficiency. A lower score means more efficient.
                  HERS 100 = standard new home. Each point below 100 represents ~1% energy savings.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </CardTitle>
        <p className="text-sm text-slate-400">
          See how energy efficiency translates to purchasing power and monthly savings
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Energy Star Certification Badge */}
          <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-900/30 to-cyan-900/30 rounded-xl neon-border-cyan">
            <div className="flex items-center gap-3">
              <div className="relative">
                <Star className="w-12 h-12 text-cyan-400" fill="currentColor" />
                <Star className="w-12 h-12 text-green-400 absolute inset-0 animate-pulse" fill="none" strokeWidth={2} />
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Energy Certified</p>
                <p className="text-lg font-bold text-cyan-400">High Efficiency Home</p>
              </div>
            </div>
            <Badge className="bg-green-500 text-slate-900 font-bold hover:bg-green-400">
              SAVE $$
            </Badge>
          </div>

          {/* Input Controls */}
          <div className="grid md:grid-cols-3 gap-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
            <div>
              <Label htmlFor="hersIndex" className="text-sm text-slate-300">
                Home HERS Index
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger className="ml-1">
                      <Info className="w-3 h-3 inline text-slate-400" />
                    </TooltipTrigger>
                    <TooltipContent className="bg-slate-800 text-slate-100 border-slate-700">
                      <p className="text-xs">Lower is better. 70 is 30% more efficient than 100.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </Label>
              <Input
                id="hersIndex"
                type="number"
                min="0"
                max="150"
                value={localHersIndex}
                onChange={(e) => {
                  setLocalHersIndex(e.target.value);
                  const val = parseInt(e.target.value);
                  if (!isNaN(val) && val >= 0) onHersIndexChange(val);
                }}
                className="mt-1 bg-slate-900/80 border-slate-600 text-slate-100 focus:border-cyan-400 focus:ring-cyan-400/30"
              />
            </div>
            <div>
              <Label htmlFor="baseline" className="text-sm text-slate-300">
                Baseline HERS (Comparison)
              </Label>
              <Input
                id="baseline"
                type="number"
                min="0"
                max="150"
                value={localBaseline}
                onChange={(e) => {
                  setLocalBaseline(e.target.value);
                  const val = parseInt(e.target.value);
                  if (!isNaN(val) && val >= 0) onBaselineChange(val);
                }}
                className="mt-1 bg-slate-900/80 border-slate-600 text-slate-100 focus:border-cyan-400 focus:ring-cyan-400/30"
              />
            </div>
            <div>
              <Label htmlFor="utilityCost" className="text-sm text-slate-300">
                Est. Annual Utility Cost
              </Label>
              <Input
                id="utilityCost"
                type="number"
                min="0"
                defaultValue="2500"
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (!isNaN(val) && val >= 0) onUtilityCostChange(val);
                }}
                className="mt-1 bg-slate-900/80 border-slate-600 text-slate-100 focus:border-cyan-400 focus:ring-cyan-400/30"
              />
            </div>
          </div>

          {/* Efficiency Rating */}
          <div className="text-center p-4 bg-gradient-to-br from-slate-900 to-slate-800 rounded-lg neon-border-cyan">
            <p className="text-sm text-slate-400 mb-2">Efficiency Rating</p>
            <Badge className={`${rating.color} text-lg px-4 py-2 font-bold`}>
              {rating.label}
            </Badge>
            <p className="text-xs text-slate-500 mt-2">
              {impact.hersIndex} HERS Index
            </p>
          </div>

          {/* Key Metrics Grid */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Monthly Savings */}
            <div className="p-4 bg-slate-900/80 rounded-lg neon-border-green">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-green-400" />
                <p className="text-sm font-medium text-green-300">Monthly Utility Savings</p>
              </div>
              <p className="text-3xl font-bold text-green-400">
                {formatCurrency(impact.monthlyUtilitySavings)}
              </p>
              <p className="text-xs text-green-500 mt-1">
                {formatCurrency(impact.annualUtilitySavings)} per year
              </p>
            </div>

            {/* Equivalent Purchase Power */}
            <div className="p-4 bg-slate-900/80 rounded-lg neon-border-cyan">
              <div className="flex items-center gap-2 mb-2">
                <TrendingDown className="w-4 h-4 text-cyan-400" />
                <p className="text-sm font-medium text-cyan-300">Equivalent Price Difference</p>
              </div>
              <p className="text-3xl font-bold text-cyan-400">
                {formatCurrency(impact.equivalentPriceDifference)}
              </p>
              <p className="text-xs text-cyan-500 mt-1">
                at ${impact.paymentFactor.toFixed(2)}/mo per $1,000
              </p>
            </div>
          </div>

          {/* Comparison Scenario */}
          <div className="border-t border-slate-700 pt-4">
            <h4 className="font-semibold mb-3 text-slate-100">Side-by-Side Comparison</h4>
            <div className="grid md:grid-cols-2 gap-4">
              {/* Standard Home */}
              <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                <p className="text-sm font-medium text-slate-300 mb-3">
                  Standard Home (HERS {impact.baselineHersIndex})
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Purchase Price:</span>
                    <span className="font-semibold text-slate-100">
                      {formatCurrency(impact.comparisonToStandard.standardHomePrice)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Monthly Mortgage:</span>
                    <span className="font-semibold text-slate-100">
                      {formatCurrency(impact.comparisonToStandard.totalMonthlyStandard - 
                        ((impact.baselineHersIndex / 100) * 2500 / 12))}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Monthly Utilities:</span>
                    <span className="font-semibold text-slate-100">
                      {formatCurrency((impact.baselineHersIndex / 100) * 2500 / 12)}
                    </span>
                  </div>
                  <div className="flex justify-between border-t border-slate-600 pt-2 font-bold">
                    <span className="text-slate-300">Total Monthly:</span>
                    <span className="text-white">{formatCurrency(impact.comparisonToStandard.totalMonthlyStandard)}</span>
                  </div>
                </div>
              </div>

              {/* Efficient Home */}
              <div className="p-4 bg-slate-900/80 rounded-lg neon-border-green">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium text-green-300">
                    Efficient Home (HERS {impact.hersIndex})
                  </p>
                  <Badge className="bg-green-500 text-slate-900 text-xs font-bold hover:bg-green-400">
                    BETTER VALUE
                  </Badge>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-green-400">Purchase Price:</span>
                    <span className="font-semibold text-slate-100">
                      {formatCurrency(impact.comparisonToStandard.efficientHomePrice)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-green-400">Monthly Mortgage:</span>
                    <span className="font-semibold text-slate-100">
                      {formatCurrency(impact.comparisonToStandard.totalMonthlyEfficient - 
                        ((impact.hersIndex / 100) * 2500 / 12))}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-green-400">Monthly Utilities:</span>
                    <span className="font-semibold text-slate-100">
                      {formatCurrency((impact.hersIndex / 100) * 2500 / 12)}
                    </span>
                  </div>
                  <div className="flex justify-between border-t border-green-500/30 pt-2 font-bold">
                    <span className="text-green-300">Total Monthly:</span>
                    <span className="text-white">{formatCurrency(impact.comparisonToStandard.totalMonthlyEfficient)}</span>
                  </div>
                </div>
              </div>
            </div>

            {impact.comparisonToStandard.netMonthlySavings > 0 && (
              <div className="mt-4 p-4 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-90">Net Monthly Savings</p>
                    <p className="text-2xl font-bold">
                      {formatCurrency(impact.comparisonToStandard.netMonthlySavings)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm opacity-90">Savings Over {30} Years</p>
                    <p className="text-xl font-bold">
                      {formatCurrency(impact.savingsOverLoanTerm)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sales Talk Example */}
          <div className="p-4 bg-slate-800/60 border-l-4 neon-border-blue rounded">
            <p className="text-sm font-semibold text-blue-300 mb-2 flex items-center gap-2">
              <span>💬</span> Sales Talk Example:
            </p>
            <p className="text-sm text-blue-200 italic leading-relaxed">
              "This home's HERS {impact.hersIndex} rating means an estimated{' '}
              <strong className="text-cyan-400">{formatCurrency(impact.annualUtilitySavings)}</strong> in annual energy savings
              — that's about <strong className="text-cyan-400">{formatCurrency(impact.monthlyUtilitySavings)}/month</strong>{' '}
              less on utilities. That monthly savings is equivalent to about{' '}
              <strong className="text-cyan-400">{formatCurrency(impact.equivalentPriceDifference)}</strong> in home price.
              So even if this home costs a bit more upfront, your overall monthly cost is actually lower."
            </p>
          </div>

          {/* Disclaimers */}
          <div className="text-xs text-slate-400 space-y-1 p-3 bg-slate-900/40 rounded border border-slate-700">
            <p className="font-semibold text-slate-300">⚠️ Important Disclaimers:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Payment factor varies with interest rates (${impact.paymentFactor.toFixed(2)}/mo per $1,000 at current rate)</li>
              <li>Actual HERS savings depend on household size, thermostat settings, and local utility rates</li>
              <li>These are estimated savings - always say "estimated" and "approximate equivalent"</li>
              <li>Complies with RESNET and lender guidelines for energy efficiency communications</li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
